import { ButtonHTMLAttributes, CSSProperties, PropsWithChildren, forwardRef } from 'react';

import clsx from 'clsx';

import { button, buttonText, buttonWithIconGap } from './Button.css';
import { ButtonSize, ButtonType } from './types';
import { buttonTextSize } from './utils';
import { Flex } from '../Flex';
import { IconLoadingLine } from '../Icon';
import { Text } from '../Text';

export interface ButtonProps
  extends PropsWithChildren<Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'prefix'>> {
  buttonType?: ButtonType;
  buttonSize?: ButtonSize;
  loading?: boolean;
  disabled?: boolean;
  /** 버튼 시작 위치에 표시할 아이콘 등 */
  prefix?: React.ReactNode;
  /** 버튼 끝 위치에 표시할 아이콘 등 */
  suffix?: React.ReactNode;
  /** 버튼 내 텍스트 중앙 정렬 여부 */
  alignCenter?: boolean;
  /** suffix close 기능 여부 */
  closable?: boolean;
  /** 버튼 너비 */
  width?: CSSProperties['width'];
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      buttonType = 'normal',
      buttonSize = 'm',
      disabled,
      loading = false,
      prefix,
      suffix,
      children,
      type = 'button',
      alignCenter = true,
      className,
      closable = false,
      width = 'fit-content',
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        className={clsx(
          button({
            buttonType,
            buttonSize,
            disabled: isDisabled,
            loading,
            iconOnly: false,
            hasPrefix: loading || Boolean(prefix),
            hasSuffix: Boolean(suffix),
            closable,
          }),
          buttonWithIconGap({ buttonSize }),
          buttonText({ buttonSize }),
          className,
        )}
        type={type}
        disabled={isDisabled}
        style={{ width }}
        {...props}
      >
        <Flex
          justify={alignCenter ? 'center' : 'start'}
          style={{ minWidth: 0, ...(alignCenter ? {} : { display: 'inline-flex', width: '100%' }) }}
          align="center"
          className={buttonWithIconGap({ buttonSize })}
        >
          {loading ? <IconLoadingLine spin /> : prefix ?? null}
          {typeof children === 'string' ? (
            <Text textStyle={buttonTextSize(buttonSize)} ellipsis showTooltipOnEllipsis>
              {children}
            </Text>
          ) : (
            children
          )}
        </Flex>
        {suffix}
      </button>
    );
  },
);
